import { create } from "zustand";
import type { ChatMessage, VisualPayload } from "../types";

interface ChatState {
  messages: ChatMessage[];
  addUserMessage: (content: string, id?: string) => void;
  addAssistantMessage: (id: string) => void;
  appendToken: (token: string, messageId: string) => void;
  setVisual: (visual: VisualPayload, messageId: string) => void;
  setStreamingDone: (messageId: string) => void;
  setError: (messageId: string, text: string) => void;
  updateLastUserMessage: (content: string) => void;
  updateMessageContent: (id: string, content: string) => void;
  clearMessages: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],

  addUserMessage: (content, id) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: id ?? crypto.randomUUID(),
          role: "user",
          content,
          timestamp: new Date().toISOString(),
        },
      ],
    })),

  addAssistantMessage: (id) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id,
          role: "assistant",
          content: "",
          timestamp: new Date().toISOString(),
          streaming: true,
        },
      ],
    })),

  appendToken: (token, messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, content: m.content + token } : m,
      ),
    })),

  setVisual: (visual, messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, visual } : m,
      ),
    })),

  setStreamingDone: (messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, streaming: false } : m,
      ),
    })),

  setError: (messageId, text) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, content: text, streaming: false } : m,
      ),
    })),

  updateLastUserMessage: (content) =>
    set((state) => {
      const idx = [...state.messages].reverse().findIndex((m) => m.role === "user");
      if (idx === -1) return state;
      const realIdx = state.messages.length - 1 - idx;
      return {
        messages: state.messages.map((m, i) =>
          i === realIdx ? { ...m, content } : m,
        ),
      };
    }),

  updateMessageContent: (id, content) =>
    set((state) => ({
      messages: state.messages.map((m) => (m.id === id ? { ...m, content } : m)),
    })),

  clearMessages: () => set({ messages: [] }),
}));
